import { ReactNode, useState } from "react";
import { ChevronRightIcon } from "../../../icons";
import { cn } from "../../../utils/class-names";
import { layout } from "../../layout";
import { MobileMenuState } from "./types";
import { match } from "ts-pattern";
import { MobileNavContext, useMobileNavContext } from "./mobile-nav-context";
import { ProtocolKey } from "@silo/types";
import { Button } from "../../button";
import { ProtocolDisplay } from "../../protocol-display";
import { ProtocolGroup } from "../../protocol-group";
import { VersionGroup } from "./version-group";

interface MobileMenuProps {
  initialState: MobileMenuState;
  primaryLinks: ReactNode;
  secondaryLinks: ReactNode;
  walletTrigger: ReactNode;
  walletFlow: ReactNode;
  protocolKey: ProtocolKey;
  setProtocolKey: (value: ProtocolKey) => void;
  runtimeProtocolKeys: ProtocolKey[];
  rewardsTrigger: ReactNode;
}

export function MobileMenu({
  primaryLinks,
  secondaryLinks,
  walletTrigger,
  walletFlow,
  initialState,
  protocolKey,
  setProtocolKey,
  runtimeProtocolKeys,
  rewardsTrigger,
}: MobileMenuProps): JSX.Element {
  const { closeDialog } = useMobileNavContext();

  const [state, setState] = useState<MobileMenuState>(initialState);

  function openWalletFlow(): void {
    setState("walletFlow");
  }

  function openVersionFlow(): void {
    setState("versionFlow");
  }

  function openProtocolFlow(): void {
    setState("protocolFlow");
  }

  function openHome(): void {
    setState("home");
  }

  const mobileNavContext: MobileNavContext = {
    closeDialog,
    openWalletFlow,
    openHome,
  };

  return (
    <MobileNavContext.Provider value={mobileNavContext}>
      <div>
        {match(state)
          .with("home", () => (
            <MenuContainer>
              <BackButton onClick={closeDialog} />

              <div className="flex flex-col self-stretch gap-16">
                <div className="flex flex-col gap-5">
                  {walletTrigger}

                  <Button
                    onClick={openVersionFlow}
                    variant="tertiary"
                    className="flex items-center justify-between gap-5 sm:self-start px-2.5"
                  >
                    v1
                    <ChevronRightIcon className="w-7 h-7" />
                  </Button>

                  <Button
                    onClick={openProtocolFlow}
                    variant="tertiary"
                    className="flex items-center justify-between gap-5 sm:self-start px-2.5"
                  >
                    <ProtocolDisplay protocolKey={protocolKey} />

                    <ChevronRightIcon className="w-7 h-7" />
                  </Button>

                  {rewardsTrigger}
                </div>

                {primaryLinks}

                {secondaryLinks}
              </div>
            </MenuContainer>
          ))
          .with("walletFlow", () => (
            <MenuContainer>
              <BackButton onClick={openHome} />

              {walletFlow}
            </MenuContainer>
          ))
          .with("versionFlow", () => (
            <MenuContainer>
              <BackButton onClick={openHome} />

              <VersionGroup onSelect={closeDialog} />
            </MenuContainer>
          ))
          .with("protocolFlow", () => (
            <MenuContainer>
              <BackButton onClick={openHome} />

              <ProtocolGroup
                runtimeProtocolKeys={runtimeProtocolKeys}
                protocolKey={protocolKey}
                setProtocol={(_protocolKey) => {
                  setProtocolKey(_protocolKey);
                  closeDialog();
                }}
              />
            </MenuContainer>
          ))
          .exhaustive()}
      </div>
    </MobileNavContext.Provider>
  );
}

interface MenuContainerProps {
  className?: string;
  children?: ReactNode;
}

export function MenuContainer({
  className,
  children,
}: MenuContainerProps): JSX.Element {
  return (
    <div
      className={layout({
        className: cn(
          "flex flex-col gap-10 items-stretch sm:items-start",
          className
        ),
      })}
    >
      {children}
    </div>
  );
}

interface BackButtonProps {
  className?: string;
  onClick: () => void;
}

export function BackButton({
  className,
  onClick,
}: BackButtonProps): JSX.Element {
  return (
    <Button variant="link" className={cn("w-fit", className)} onClick={onClick}>
      Back
    </Button>
  );
}

interface ChevronProps {
  className?: string;
}

export function Chevron({ className }: ChevronProps): JSX.Element {
  return <ChevronRightIcon className={cn("w-7 h-7", className)} />;
}
